import { RouteComponentProps } from '@reach/router'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Title } from '../components/ui'
import { Descriptions, Button, Col, Form, Input, Modal, Row, Select, Spin, Radio, Tag, Checkbox } from 'antd'
import { useLoading, useSMS } from '../common/hooks'
import { getProfileInfo, modifyProfile, smsModifyProfile } from '../services/profile'
import { required, validateMessages } from '../common/validate'
import { utc } from 'moment'
import options from '../assets/ml_addr.json'
import Male from '../assets/Male.png'
import Female from '../assets/Female.png'
import { api } from '../common/api'
import useSWR from 'swr'
import { USER_SESSION_EKY } from '../common/config'
import { encryptPhone } from '../common/helper'

const { Option } = Select
const StyledProfile = styled.div`
  margin: 43px auto;

  .profile {
    display: flex;

    label {
      font-weight: bold;
    }

    .profile__img {
      width: 220px;
      margin: 0 auto;
    }

    &__item {
      text-decoration: underline;
    }

    .ant-form-item-control-input-content {
      cursor: pointer;
    }

    .desc {
      font-size: 11px;

      .hide-label {
        .ant-descriptions-item-label {
          opacity: 0;
        }
      }

      .ant-descriptions-item-label {
        width: 130px;
        font-weight: bold;
      }

      .ant-descriptions-item-content {
        color: #2c4b9b;
      }
    }

    &-edit {
      width: 100%;
      font-size: 16px;
      margin-top: 1em;

      label {
        display: none;
      }

      label.ant-checkbox-wrapper {
        display: inline-block;
      }

      .ant-radio-wrapper {
        display: inline-block !important;
      }

      .ant-form-item {
        margin-bottom: 5px !important;
      }

      button {
        width: 120px;
        margin-right: 20px;
      }

      tr {
        background-color: #f9bb19;
        td {
          padding: 10px 10px;
        }

        td:first-of-type {
          background-color: #f9bb19;
          text-align: right;
          padding-right: 1em;
          white-space: nowrap;
          width: 140px;
        }

        td:last-of-type {
          background-color: rgb(249, 239, 211);
          padding-left: 20px;
        }
      }

      tr:first-of-type {
        td {
          padding-top: 30px;
        }
      }
      tr:last-of-type {
        td {
          padding-bottom: 30px;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    margin-top: 15px;

    .profile-edit label {
      display: inline-flex;

      &.ant-checkbox-wrapper {
        display: inline-flex;
      }
    }
  }
`

export const useProfile = () => {
  const [state, setState] = useState<ProfileInfo>({
    basicInfo: {},
    bankInfo: {},
    empInfo: {},
    sex: 'male',
    name: '',
  } as ProfileInfo)
  const { loading, start, finish } = useLoading()
  const isMounted = useRef(false)
  useEffect(() => {
    const fetch = async () => {
      start()
      const data = await getProfileInfo()
      setState(data)
      finish()
    }
    if (!isMounted.current) {
      isMounted.current = true
      fetch()
    }
  }, [finish, start])

  return {
    state,
    loading,
  }
}

const ProfileView: React.FC<{
  state: ProfileInfo
  onEdit: (type: EditType) => void
}> = ({ state, onEdit }) => {
  return (
    <div className="w-full mt-5">
      <div className="divide-y">
        <p className="font-bold flex items-center justify-between">
          Basic Information
          <Button type="link" danger onClick={() => onEdit('basic')}>
            Edit
          </Button>
        </p>
        <Descriptions column={1} className="desc pt-2">
          <Descriptions.Item label="Address">
            {`${state.basicInfo.address},${state.basicInfo.street ? state.basicInfo.street + ',' : ''}${state.basicInfo.postcode},${state.basicInfo.city},${state.basicInfo.state}`}
          </Descriptions.Item>
          {Number(state.basicInfo.copdTag) === 1 && (
            <Descriptions.Item label="null" className="hide-label">
              <Tag color="cyan">correspondence address</Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Email">{state.basicInfo.email}</Descriptions.Item>
          <Descriptions.Item label="Mobile No">{encryptPhone(state.basicInfo.mobile)}</Descriptions.Item>
        </Descriptions>
      </div>
      <div className="divide-y mt-8">
        <p className="font-bold flex items-center justify-between">
          Employment Information
          <Button type="link" danger onClick={() => onEdit('emp')}>
            Edit
          </Button>
        </p>
        <Descriptions column={1} className="desc pt-2">
          <Descriptions.Item label="Company Name">{state.empInfo.companyName}</Descriptions.Item>
          <Descriptions.Item label="Address">
            {`${state.empInfo.address},${state.empInfo.street ? state.empInfo.street + ',' : ''},${state.empInfo.postcode},${state.empInfo.city},${state.empInfo.state}`}
          </Descriptions.Item>
          {Number(state.empInfo.copdTag) === 1 && (
            <Descriptions.Item label="null" className="hide-label">
              <Tag color="cyan">correspondence address</Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Office No">{encryptPhone(state.empInfo.phone)}</Descriptions.Item>
        </Descriptions>
      </div>
      <div className="divide-y mt-8">
        <p className="font-bold flex items-center justify-between">
          Bank Information
          <Button type="link" danger onClick={() => onEdit('bank')}>
            Edit
          </Button>
        </p>
        <Descriptions column={1} className="desc pt-2">
          <Descriptions.Item label="Bank">{state.bankInfo.bankName}</Descriptions.Item>
          <Descriptions.Item label="Bank Acc. No">{state.bankInfo.accNo}</Descriptions.Item>
        </Descriptions>
      </div>
    </div>
  )
}

const createInitialValue = (state: ProfileInfo, type: EditType | '') => {
  if (!type) return {}
  switch (type) {
    case 'basic':
      return {
        mode: 1,
        ext: '',
        mobile: encryptPhone(state.basicInfo.mobile),
        email: state.basicInfo.email,
        state: state.basicInfo.state,
        address: state.basicInfo.address.toUpperCase(),
        postcode: state.basicInfo.postcode,
        city: state.basicInfo.city,
        street: state.basicInfo.street,
        copdTag: Number(state.basicInfo.copdTag) === 1,
      }
    case 'emp':
      return {
        mode: 2,
        phone: encryptPhone(state.empInfo.phone),
        companyName: state.empInfo.companyName,
        state: state.empInfo.state,
        address: state.empInfo.address,
        postcode: state.empInfo.postcode,
        city: state.empInfo.city,
        street: state.empInfo.street,
        copdTag: Number(state.empInfo.copdTag) === 1,
      }
    case 'bank':
      return {
        mode: 3,
        accNo: state.bankInfo.accNo, // mode=3必填
        // 银行账户类型
        accType: state.bankInfo.type || 'Current', // mode=3必填
        // 银行名称
        bank: state.bankInfo.bankCode, // mode=3必填
      }
  }
}

interface AddrItem {
  value: string
  label: string
  children?: AddrItem[]
}

const SMSModal: React.FC<{
  visible: boolean
  onSubmit: (sms: string) => Promise<any>
  onClose: () => void
  mode?: 'addr' | 'bank'
}> = ({ visible, onSubmit, onClose, mode }) => {
  const [form] = Form.useForm()
  const { loading, start, finish } = useLoading()
  const userInfo = JSON.parse(sessionStorage.getItem(USER_SESSION_EKY) || '{}')
  const { sending, sendSMS, remaining, smsSended, resetInterval } = useSMS(
    () => smsModifyProfile({ mobile: userInfo.tel, mode: mode === 'bank' ? 2 : 0 }),
    (data) => {}
  )

  const submit = async () => {
    start()
    try {
      await onSubmit(form.getFieldValue('sms'))
      finish()
      Modal.success({
        content: 'Your request has been successfully processed.',
        okText: 'Return',
        onOk() {
          window.history.go(0)
        },
      })
    } catch (error) {
      finish()
    }
  }

  const close = () => {
    finish()
    resetInterval()
    onClose()
  }

  return (
    <Modal visible={visible} footer={null} onCancel={close} destroyOnClose>
      <Form className="mt-6" form={form} onFinish={submit}>
        <Form.Item label="One Time Password" name="sms" rules={[required]}>
          <Row align="middle">
            <Col span="16">
              <Input />
            </Col>
            <Col span="8">
              <Button type="link" loading={sending} onClick={sendSMS}>
                {smsSended ? `expiring ${utc(remaining * 60 * 1000).format('HH:mm')}` : 'Request OTP'}
              </Button>
            </Col>
          </Row>
        </Form.Item>
        <Row gutter={15} align="middle" justify="end">
          <Col>
            <Button onClick={close}>Cancel</Button>
          </Col>
          <Col>
            <Button htmlType="submit" type="primary" loading={loading}>
              Submit
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

const ProfileBasicEdit: React.FC<{
  type: EditType | ''
  state: ProfileInfo
  setEditVisible: (visible: boolean) => void
}> = ({ type, state, setEditVisible }) => {
  const [form] = Form.useForm()
  const initialValues = createInitialValue(state, type)
  const [cityList, setCityList] = useState([] as Array<AddrItem>)
  const [stateList, setStateList] = useState([] as Array<AddrItem>)
  const [smsModalVisible, setSmsModalVisible] = useState(false)

  const onFinish = async () => {
    setSmsModalVisible(true)
  }

  const onSubmit = async (sms: string) => {
    const data = form.getFieldsValue()
    data.copdTag = data.copdTag ? 1 : 0
    data.street = (data.street || '').toUpperCase()
    data.address = (data.address || '').toUpperCase()
    data.mode = initialValues.mode
    if (String(data.mobile).includes('*')) {
      data.mobile = state.basicInfo.mobile
    }
    await modifyProfile({ ...data, sms })
  }

  return (
    <>
      <Form form={form} initialValues={initialValues} onFinish={onFinish} validateMessages={validateMessages}>
        <table className="profile-edit">
          <tbody>
            <tr>
              <td className="hidden sm:block"></td>
              <td>Please update your latest information</td>
            </tr>
            <tr>
              <td className="hidden sm:block">Address:</td>
              <td>
                <Form.Item label="address" name="address" rules={[required]}>
                  <Input placeholder="address" className="uppercase" />
                </Form.Item>
                <Form.Item label="street" name="street">
                  <Input placeholder="street" className="uppercase" />
                </Form.Item>
                <Row>
                  <Col span={24} sm={8}>
                    <Form.Item label="postcode" name="postcode" rules={[required]}>
                      <Select
                        showSearch
                        onChange={(value) => {
                          setCityList((options.find((item) => item.value === String(value)) || {}).children || [])
                          setStateList([])
                          form.setFieldsValue({ city: '', state: '' })
                        }}
                      >
                        {options.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24} sm={8}>
                    <Form.Item label="city" name="city" rules={[required]}>
                      <Select
                        showSearch
                        onChange={(value) => {
                          setStateList((cityList.find((item) => item.value === String(value)) || {}).children || [])
                          form.setFieldsValue({ state: '' })
                        }}
                      >
                        {cityList.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24} sm={8}>
                    <Form.Item label="state" name="state" rules={[required]}>
                      <Select showSearch>
                        {stateList.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="copdTag" valuePropName="checked">
                  <Checkbox>use as correspondence address</Checkbox>
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block">Email:</td>
              <td>
                <Form.Item label="email" name="email" rules={[required]}>
                  <Input />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block">Mobile No:</td>
              <td>
                <Form.Item label="mobile" name="mobile" rules={[required]}>
                  <Input />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block"></td>
              <td>
                <Button type="primary" danger onClick={() => setEditVisible(false)}>
                  Cancel
                </Button>
                <Button htmlType="submit" type="primary">
                  Save
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </Form>
      <SMSModal visible={smsModalVisible} onClose={() => setSmsModalVisible(false)} onSubmit={onSubmit} />
    </>
  )
}

const ProfileEmpEdit: React.FC<{
  type: EditType | ''
  state: ProfileInfo
  setEditVisible: (visible: boolean) => void
}> = ({ type, state, setEditVisible }) => {
  const [form] = Form.useForm()
  const initialValues = createInitialValue(state, type)
  const [smsModalVisible, setSmsModalVisible] = useState(false)
  const [cityList, setCityList] = useState([] as Array<AddrItem>)
  const [stateList, setStateList] = useState([] as Array<AddrItem>)

  const onFinish = async () => {
    setSmsModalVisible(true)
  }

  const onSubmit = async (sms: string) => {
    const data = form.getFieldsValue()
    data.copdTag = data.copdTag ? 1 : 0
    data.street = (data.street || '').toUpperCase()
    data.address = (data.address || '').toUpperCase()
    data.mode = initialValues.mode
    if (String(data.phone).includes('*')) {
      data.phone = state.empInfo.phone
    }
    await modifyProfile({ ...data, sms })
  }

  return (
    <>
      <Form form={form} initialValues={initialValues} onFinish={onFinish} validateMessages={validateMessages}>
        <table className="profile-edit">
          <tbody>
            <tr>
              <td className="hidden sm:block"></td>
              <td>Please update your Employment information</td>
            </tr>
            <tr>
              <td className="hidden sm:block">Company Name:</td>
              <td>
                <Form.Item label="Company Name" name="companyName" rules={[required]}>
                  <Input placeholder="Please Enter your Company Name" />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block">Office No.:</td>
              <td>
                <Form.Item label="Office No." name="phone" rules={[required]}>
                  <Input placeholder="Please Enter your Office No." />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block">Address:</td>
              <td>
                <Form.Item label="address" name="address" rules={[required]}>
                  <Input placeholder="address" className="uppercase" />
                </Form.Item>
                <Form.Item label="street" name="street">
                  <Input placeholder="street" className="uppercase" />
                </Form.Item>
                <Row>
                  <Col span={24} sm={8}>
                    <Form.Item label="postcode" name="postcode" rules={[required]}>
                      <Select
                        showSearch
                        onChange={(value) => {
                          setCityList((options.find((item) => item.value === String(value)) || {}).children || [])
                          setStateList([])
                          form.setFieldsValue({
                            city: '',
                            state: '',
                          })
                        }}
                      >
                        {options.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24} sm={8}>
                    <Form.Item label="city" name="city" rules={[required]}>
                      <Select
                        showSearch
                        onChange={(value) => {
                          setStateList((cityList.find((item) => item.value === String(value)) || {}).children || [])
                          form.setFieldsValue({ state: '' })
                        }}
                      >
                        {cityList.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={24} sm={8}>
                    <Form.Item label="state" name="state" rules={[required]}>
                      <Select showSearch>
                        {stateList.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block"></td>
              <td>
                <Form.Item name="copdTag" valuePropName="checked">
                  <Checkbox>use as correspondence address</Checkbox>
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block"></td>
              <td>
                <Button type="primary" danger onClick={() => setEditVisible(false)}>
                  Cancel
                </Button>
                <Button htmlType="submit" type="primary">
                  Save
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </Form>
      <SMSModal visible={smsModalVisible} onClose={() => setSmsModalVisible(false)} onSubmit={onSubmit} />
    </>
  )
}

interface BankItem {
  bankName: string
  bankCode: string
}

const ProfileBankEdit: React.FC<{
  type: EditType | ''
  state: ProfileInfo
  setEditVisible: (visible: boolean) => void
}> = ({ type, state, setEditVisible }) => {
  const [form] = Form.useForm()
  const { data } = useSWR<Array<BankItem>>('/customer/profile/banks', api.get)
  const initialValues = createInitialValue(state, type)
  const [smsModalVisible, setSmsModalVisible] = useState(false)

  const onFinish = async () => {
    setSmsModalVisible(true)
  }

  const onSubmit = async (sms: string) => {
    const data = form.getFieldsValue()
    data.mode = initialValues.mode
    await modifyProfile({ ...data, sms })
  }

  if (!data) {
    return <Spin />
  }

  return (
    <>
      <Form form={form} initialValues={initialValues} onFinish={onFinish} validateMessages={validateMessages}>
        <table className="profile-edit">
          <tbody>
            <tr>
              <td className="hidden sm:block"></td>
              <td>Please update your bank information</td>
            </tr>
            <tr>
              <td className="hidden sm:block">Bank:</td>
              <td>
                <Form.Item label="bank" name="bank" rules={[required]}>
                  <Select showSearch>
                    {data.map((item) => (
                      <Option key={item.bankCode} value={item.bankCode}>
                        {item.bankName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block">Bank Type:</td>
              <td>
                <Form.Item label="accType" name="accType" rules={[required]}>
                  <Radio.Group>
                    <Radio value="Current">Current</Radio>
                    <Radio value="Saving">Saving</Radio>
                  </Radio.Group>
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block">Bank Acc No:</td>
              <td>
                <Form.Item label="accNo" name="accNo" rules={[required]}>
                  <Input placeholder="please enter Bank Acc No." />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td className="hidden sm:block"></td>
              <td>
                <Button type="primary" danger onClick={() => setEditVisible(false)}>
                  Cancel
                </Button>
                <Button htmlType="submit" type="primary">
                  Save
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </Form>
      <SMSModal visible={smsModalVisible} onClose={() => setSmsModalVisible(false)} onSubmit={onSubmit} mode="bank" />
    </>
  )
}

type EditType = 'basic' | 'emp' | 'bank'

const EditComps: Record<EditType, typeof ProfileBasicEdit | typeof ProfileEmpEdit | typeof ProfileBankEdit> = {
  basic: ProfileBasicEdit,
  emp: ProfileEmpEdit,
  bank: ProfileBankEdit,
}

export const Profile: React.FC<RouteComponentProps> = () => {
  const [editVisible, setEditVisible] = useState(false)
  const { state, loading } = useProfile()
  const [editType, setEditType] = useState<EditType | ''>('')

  const onEdit = (type: EditType) => {
    setEditVisible(true)
    setEditType(type)
  }

  const Edit = editType ? EditComps[editType] : ''

  return (
    <StyledProfile>
      <Spin spinning={loading}>
        <div className="profile">
          <div className="hidden lg:block w-1/3">
            {state.sex === 'female' ? <img className="profile__img" src={Female} alt="female" /> : <img className="profile__img" src={Male} alt="male" />}
          </div>
          <div className="w-full pl-0 lg:pl-16">
            <img className="inline-block w-8 sm:hidden float-right" src={state.sex === 'female' ? Female : Male} alt={state.sex} />
            <Title className="mb-7">My Profile</Title>
            <span className="text-xl pl-2">{state.name}</span>
            {editVisible && Edit ? <Edit type={editType} state={state} setEditVisible={setEditVisible} /> : <ProfileView state={state} onEdit={onEdit} />}
          </div>
        </div>
      </Spin>
    </StyledProfile>
  )
}
